import React, { useState } from 'react';
import { Menu, X, Phone, ChevronDown, ChevronUp, Calculator } from 'lucide-react';
import { Button } from './ui/button';
import { useNavigate, useLocation } from 'react-router-dom';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [destinationsOpen, setDestinationsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleNavClick = (href) => {
    if (href.startsWith('#')) {
      if (location.pathname === '/') {
        const element = document.querySelector(href);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      } else {
        navigate('/' + href);
      }
    } else {
      navigate(href);
    }
    setIsOpen(false);
    setServicesOpen(false);
    setDestinationsOpen(false);
  };

  const toggleServices = () => {
    setServicesOpen(!servicesOpen);
    if (destinationsOpen) setDestinationsOpen(false);
  };

  const toggleDestinations = () => {
    setDestinationsOpen(!destinationsOpen);
    if (servicesOpen) setServicesOpen(false);
  };

  const navLinks = [
    { name: 'Home', href: '/' },
    { name: 'Track Shipment', href: '/track-shipment' },
    { name: 'Pricing', href: '/shipping-cost-canada-to-india' },
    { name: 'Contact', href: '/contact' }
  ];

  const toolsLinks = [
    { name: 'Calculate Volume Weight', href: '/actual-weight-vs-volumetric-weight', icon: Calculator }
  ];

  const servicesLinks = [
    { name: 'International Courier & Cargo', href: '/services' },
    { name: 'Supplements & Protein Shipping', href: '/shipping-supplements-protein' },
    { name: 'Electronics & Battery Shipping', href: '/electronics-battery-shipping' },
    { name: 'International Perfume Shipping', href: '/international-perfume-shipping' },
    { name: 'International Barrel Shipping', href: '/international-barrel-shipping' },
    { name: 'Relocation Shipping', href: '/relocation-shipping-canada-to-india' },
    { name: 'Document Shipping', href: '/documents-to-india-from-canada' },
    { name: 'All Services', href: '/services' }
  ];

  const destinationsLinks = [
    { name: 'Shipping Canada to India', href: '/shipping-to-india-from-canada' },
    { name: 'Shipping Canada to UK', href: '/shipping-canada-to-uk' },
    { name: 'Shipping Canada to UAE/Dubai', href: '/shipping-canada-to-uae' },
    { name: 'Shipping Canada to Nepal', href: '/shipping-canada-to-nepal' },
    { name: 'Shipping Canada to Pakistan', href: '/shipping-canada-to-pakistan' },
    { name: 'Shipping Canada to Australia', href: '/shipping-canada-to-australia' },
    { name: 'Shipping Canada to Ukraine', href: '/shipping-canada-to-ukraine' },
    { name: 'Shipping Canada to Philippines', href: '/shipping-canada-to-philippines' },
    { name: 'Shipping Canada to Africa', href: '/shipping-canada-to-africa' },
    { name: 'Shipping Canada to Caribbean', href: '/shipping-canada-to-caribbean' },
    { name: 'All Shipping Destinations', href: '/shipping-destinations' }
  ];

  return (
    <nav className="bg-gray-900 shadow-lg fixed w-full top-0 z-50 border-b border-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <div className="flex-shrink-0 flex items-center cursor-pointer" onClick={() => navigate('/')}>
            <img 
              src="https://customer-assets.emergentagent.com/job_angry-ellis-7/artifacts/rd8nrfx9_IMG_7688.png" 
              alt="Shippzy Logo" 
              className="h-14 w-14 rounded-full"
            />
            <div className="ml-3">
              <div className="text-2xl font-bold text-white">SHIPPZY</div>
              <p className="text-xs text-yellow-400">Ship with Shippzy</p>
            </div>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <button
                key={link.name}
                onClick={() => handleNavClick(link.href)}
                className="text-gray-300 hover:text-red-500 transition-colors duration-200 font-medium cursor-pointer"
              >
                {link.name}
              </button>
            ))}
          </div>

          {/* Contact & CTA */}
          <div className="hidden md:flex items-center space-x-4">
            <div className="flex items-center text-gray-300">
              <Phone className="w-4 h-4 mr-2 text-red-500" />
              <span className="text-sm font-medium">+1 (437) 616-0758</span>
            </div>
            <Button 
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={() => handleNavClick('/contact')}
            >
              Get Quote
            </Button>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-gray-300 hover:text-red-500 transition-colors"
              aria-label="Toggle menu"
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isOpen && (
        <div className="md:hidden bg-gray-800 border-t border-gray-700 max-h-[calc(100vh-80px)] overflow-y-auto">
          <div className="px-2 pt-2 pb-3 space-y-1">
            {/* Home */}
            <button
              onClick={() => handleNavClick('/')}
              className="block w-full text-left px-4 py-3 text-gray-300 hover:text-red-500 hover:bg-gray-700 rounded-md transition-colors font-medium"
            >
              Home
            </button>

            {/* Services Expandable Section */}
            <div className="border-t border-gray-700 pt-1">
              <button
                onClick={toggleServices}
                className="flex items-center justify-between w-full px-4 py-3 text-gray-300 hover:text-red-500 hover:bg-gray-700 rounded-md transition-colors font-medium"
              >
                <span>Services</span>
                {servicesOpen ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
              </button>
              
              {servicesOpen && (
                <div className="bg-gray-750 pl-4 space-y-1 animate-in slide-in-from-top duration-200">
                  {servicesLinks.map((link) => (
                    <button
                      key={link.name}
                      onClick={() => handleNavClick(link.href)}
                      className="block w-full text-left px-4 py-2.5 text-sm text-gray-400 hover:text-yellow-400 hover:bg-gray-700 rounded-md transition-colors"
                    >
                      {link.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Shipping Destinations Expandable Section */}
            <div className="border-t border-gray-700 pt-1">
              <button
                onClick={toggleDestinations}
                className="flex items-center justify-between w-full px-4 py-3 text-gray-300 hover:text-red-500 hover:bg-gray-700 rounded-md transition-colors font-medium"
              >
                <span>Shipping Destinations</span>
                {destinationsOpen ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
              </button>
              
              {destinationsOpen && (
                <div className="bg-gray-750 pl-4 space-y-1 animate-in slide-in-from-top duration-200">
                  {destinationsLinks.map((link) => (
                    <button
                      key={link.name}
                      onClick={() => handleNavClick(link.href)}
                      className="block w-full text-left px-4 py-2.5 text-sm text-gray-400 hover:text-yellow-400 hover:bg-gray-700 rounded-md transition-colors"
                    >
                      {link.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Track Shipment */}
            <button
              onClick={() => handleNavClick('/track-shipment')}
              className="block w-full text-left px-4 py-3 text-gray-300 hover:text-red-500 hover:bg-gray-700 rounded-md transition-colors font-medium border-t border-gray-700 pt-2"
            >
              Track Shipment
            </button>

            {/* Pricing */}
            <button
              onClick={() => handleNavClick('/shipping-cost-canada-to-india')}
              className="block w-full text-left px-4 py-3 text-gray-300 hover:text-red-500 hover:bg-gray-700 rounded-md transition-colors font-medium"
            >
              Pricing
            </button>

            {/* Contact */}
            <button
              onClick={() => handleNavClick('/contact')}
              className="block w-full text-left px-4 py-3 text-gray-300 hover:text-red-500 hover:bg-gray-700 rounded-md transition-colors font-medium"
            >
              Contact
            </button>

            {/* Blog */}
            <button
              onClick={() => handleNavClick('/blog')}
              className="block w-full text-left px-4 py-3 text-gray-300 hover:text-red-500 hover:bg-gray-700 rounded-md transition-colors font-medium"
            >
              Blog
            </button>

            {/* Calculate Volume Weight - NEW */}
            <button
              onClick={() => handleNavClick('/actual-weight-vs-volumetric-weight')}
              className="block w-full text-left px-4 py-3 text-yellow-400 hover:text-yellow-300 hover:bg-gray-700 rounded-md transition-colors font-semibold border border-yellow-600/30"
            >
              <div className="flex items-center gap-2">
                <Calculator size={18} />
                <span>CALCULATE VOLUME WEIGHT</span>
              </div>
            </button>

            {/* Phone & Get Quote Button */}
            <div className="px-4 py-3 border-t border-gray-700 mt-2 space-y-3">
              <div className="flex items-center text-gray-300">
                <Phone className="w-4 h-4 mr-2 text-red-500" />
                <span className="text-sm">+1 (437) 616-0758</span>
              </div>
              <Button 
                className="w-full bg-red-600 hover:bg-red-700 text-white shadow-lg"
                onClick={() => handleNavClick('/contact')}
              >
                Get Quote
              </Button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
